/**
 * Build the onRetry callback used by <LiveDataStatus />.
 *
 * Rule: if any feed has an error, only refetch the failing feeds.
 * If everything is healthy, treat the click as a manual refresh and
 * refetch all three.
 *
 * Returns a Promise that resolves once every triggered refetch has
 * settled, so callers can keep the Retry button disabled for the full
 * round-trip instead of flipping it back on the next render.
 */
export interface RetryableQuery {
  error: unknown;
  refetch: () => unknown;
}

export interface LiveDataRetryInput {
  weather: RetryableQuery;
  marine: RetryableQuery;
  tides: RetryableQuery;
}

export async function runLiveDataRetry({ weather, marine, tides }: LiveDataRetryInput): Promise<void> {
  const anyFailed = Boolean(weather.error || marine.error || tides.error);
  const pending: Array<unknown> = [];
  if (anyFailed) {
    if (weather.error) pending.push(weather.refetch());
    if (marine.error) pending.push(marine.refetch());
    if (tides.error) pending.push(tides.refetch());
  } else {
    pending.push(weather.refetch(), marine.refetch(), tides.refetch());
  }
  await Promise.allSettled(pending);
}

export function buildLiveDataRetry(input: LiveDataRetryInput) {
  return () => runLiveDataRetry(input);
}
